import React, { useState, useEffect } from "react";
import { useSnackbar } from "notistack";
import AppLayout from "../../components/AppLayout/AppLayout";
import httpRequest from "../../httpRequest";
import {
  Container,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Modal,
  Switch,
  IconButton, FormControlLabel
} from "@mui/material";
import Autocomplete from '@mui/material/Autocomplete'; // Import Autocomplete component
import TemplateEditor from "../../components/TemplateEditor/TemplateEditor";
import CloseIcon from "@mui/icons-material/Close";
import ButtonOutlined from "../StyledComponents/ButtonOutlined";
import ButtonContained from "../StyledComponents/ButtonContained";

const TemplatesCard = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [templates, setTemplates] = useState([]); // State for template data
  const [vendors, setVendors] = useState([]); // State for vendor data
   const [searchInput, setSearchInput] = useState("");
  const [selectedTemplate, setSelectedTemplate] = useState(null); // State for selected template for editing or viewing
  const [openEditDialog, setOpenEditDialog] = useState(false); // State for edit dialog
  const [openCreateDialog, setOpenCreateDialog] = useState(false); // State for new template dialog
  const [openViewModal, setOpenViewModal] = useState(false); // State for view modal
  const [templateName, setTemplateName] = useState(""); // For renaming/editing template
  const [vendor, setVendor] = useState(null); // Vendor object for editing template
  const [isDefault, setIsDefault] = useState(false); // State for default switch
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
const [templateToDelete, setTemplateToDelete] = useState(null);


  // Fetch templates and vendors on component mount
  useEffect(() => {
    fetchTemplates();
    fetchVendors();
  }, []);

  // Fetch all templates
  const fetchTemplates = async () => {
    try {
      const response = await httpRequest.get(`${process.env.REACT_APP_BACKEND_URL}/template/all`);
      setTemplates(response.data); // Store templates in state
    } catch (error) {
      enqueueSnackbar("Error fetching templates", { variant: "error" });
    }
  };

  // Fetch all vendors
  const fetchVendors = async () => {
    try {
      const response = await httpRequest.get(`${process.env.REACT_APP_BACKEND_URL}/get-vendors`);
      setVendors(response.data.vendors); // Store vendors in state
    } catch (error) {
      enqueueSnackbar("Error fetching vendors", { variant: "error" });
    }
  };

   // Handle search input change
   const handleSearchInputChange = (event) => {
    setSearchInput(event.target.value);
  };

  // Filter templates based on search input
  const filteredTemplates = templates.filter((template) => {
    const templateNameMatch = template.template_name.toLowerCase().includes(searchInput.toLowerCase());
    const vendorNameMatch = vendors.find(v => v.id === template.vendor_id)?.vendor_name.toLowerCase().includes(searchInput.toLowerCase());
    return templateNameMatch || vendorNameMatch;
  });

  // Handle opening the edit dialog
  const handleEditClick = (template) => {
    setSelectedTemplate(template);
    setTemplateName(template.template_name);
    setVendor(vendors.find(v => v.id === template.vendor_id)); // Find the vendor object
    setIsDefault(template.default); // Set default switch to current value
    setOpenEditDialog(true);
  };

  // Handle opening the view modal for TemplateEditor
  const handleViewClick = (template) => {
    setSelectedTemplate(template);
    setOpenViewModal(true); // Open modal
  };

  // Handle opening the new template dialog
  const handleOpenCreateDialog = () => {
    setTemplateName(""); // Clear input fields
    setVendor(null); // Clear vendor selection
    setIsDefault(false); // Default switch off for new templates
    setOpenCreateDialog(true);
  };

// Handle saving the changes to template details
const handleSaveEdit = async () => {
  try {
    await httpRequest.put(`${process.env.REACT_APP_BACKEND_URL}/template/${selectedTemplate.id}/edit_details`, {
      template_name: templateName,
      vendor_id: vendor ? vendor.id : null, // Ensure vendor_id is sent as null if no vendor is selected
      default: isDefault, // Include default status
    });
    
    enqueueSnackbar("Template updated successfully", { variant: "success" });
    setOpenEditDialog(false);
    fetchTemplates(); // Refresh the template list
  } catch (error) {
    enqueueSnackbar("Error updating template", { variant: "error" });
  }
};


  // Handle saving a new template
const handleSaveNewTemplate = async () => {
  try {
    await httpRequest.post(`${process.env.REACT_APP_BACKEND_URL}/template/add`, {
      template_name: templateName,
      vendor_id: vendor ? vendor.id : null, // Ensure vendor_id is sent as null if no vendor is selected
      default: isDefault, // Include default status
    });
    
    enqueueSnackbar("New template created successfully", { variant: "success" });
    setOpenCreateDialog(false);
    fetchTemplates(); // Refresh the template list
  } catch (error) {
    enqueueSnackbar("Error creating new template", { variant: "error" });
  }
};

const handleDeleteClick = (template) => {
  setTemplateToDelete(template); // Store the template to be deleted
  setOpenDeleteDialog(true); // Open the confirmation dialog
};

const handleDeleteConfirm = async () => {
  try {
    await httpRequest.delete(`${process.env.REACT_APP_BACKEND_URL}/template/${templateToDelete.id}`);
    enqueueSnackbar("Template deleted successfully", { variant: "success" });
    fetchTemplates(); // Refresh the template list
  } catch (error) {
    enqueueSnackbar("Can't delete default template", { variant: "error" });
  } finally {
    setOpenDeleteDialog(false); // Close the dialog
    setTemplateToDelete(null); // Clear the template
  }
};

const handleDeleteCancel = () => {
  setOpenDeleteDialog(false);
  setTemplateToDelete(null);
};



  return (
    <>
     <div style={{ padding: '20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>


     <input
        placeholder="Search"
        value={searchInput}
        onChange={handleSearchInputChange}
        style={{ flex: "1", padding: "10px", marginRight: "20px" }}
      />
      <ButtonContained onClick={handleOpenCreateDialog}>
        New Template
      </ButtonContained>
        </div>
        <div style={{ padding: '20px' }}>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontFamily: '"SapFiori72", sans-serif' }}>Template Name</TableCell>
              <TableCell sx={{ fontFamily: '"SapFiori72", sans-serif' }}>Vendor Name</TableCell> {/* Updated to show Vendor Name */}
              <TableCell sx={{ fontFamily: '"SapFiori72", sans-serif' }}>Default</TableCell>
              <TableCell sx={{ fontFamily: '"SapFiori72", sans-serif' }}>Created At</TableCell>
              <TableCell sx={{ fontFamily: '"SapFiori72", sans-serif' }}>Updated At</TableCell>
              <TableCell sx={{ fontFamily: '"SapFiori72", sans-serif' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredTemplates.map((template) => (
              <TableRow key={template.id}>
                <TableCell sx={{ fontFamily: '"SapFiori72", sans-serif' }}>{template.template_name}</TableCell>
                <TableCell sx={{ fontFamily: '"SapFiori72", sans-serif' }}>{vendors.find(v => v.id === template.vendor_id)?.vendor_name || "N/A"}</TableCell> {/* Show Vendor Name */}
                <TableCell sx={{ fontFamily: '"SapFiori72", sans-serif' }}>{template.default ? "Yes" : "No"}</TableCell>
                <TableCell sx={{ fontFamily: '"SapFiori72", sans-serif' }}>{new Date(template.created_at).toLocaleString()}</TableCell>
                <TableCell sx={{ fontFamily: '"SapFiori72", sans-serif' }}>{new Date(template.updated_at).toLocaleString()}</TableCell>
                <TableCell>
                  <ButtonOutlined onClick={() => handleEditClick(template)} style={{ marginRight: 8 }}>Edit</ButtonOutlined>
                  <ButtonContained onClick={() => handleViewClick(template)} style={{ marginRight: 8 }}>View</ButtonContained>
                  <ButtonOutlined onClick={() => handleDeleteClick(template)}>Delete</ButtonOutlined> {/* Add Delete Button */}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      </div>

      {/* Edit Dialog */}
      <Dialog open={openEditDialog} onClose={() => setOpenEditDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle  sx={{ fontFamily: '"SapFiori72", sans-serif' }}>Edit Template Details</DialogTitle>
        <DialogContent>
          <TextField
            label="Template Name"
            value={templateName}
            onChange={(e) => setTemplateName(e.target.value)}
            fullWidth
            margin="normal"
          />
          <Autocomplete
  options={vendors}
  getOptionLabel={(option) => option ? `${option.vendor_code} - ${option.vendor_name}` : ""}
  onChange={(event, newValue) => setVendor(newValue ? newValue : null)} // Set to null if cleared
  renderInput={(params) => <TextField {...params} label="Vendor" margin="normal" fullWidth />}
  value={vendor}
  isOptionEqualToValue={(option, value) => option?.id === value?.id}
/>

{selectedTemplate && !selectedTemplate.default && (
    <FormControlLabel
        control={
            <Switch
                checked={isDefault}
                onChange={(e) => setIsDefault(e.target.checked)}
                name="isDefault"
            />
        }
        label="Set as Default"
    />
)}

        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEditDialog(false)}>Cancel</Button>
          <Button onClick={handleSaveEdit}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* Create New Template Dialog */}
      <Dialog open={openCreateDialog} onClose={() => setOpenCreateDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle  sx={{ fontFamily: '"SapFiori72", sans-serif' }}>Create New Template</DialogTitle>
        <DialogContent>
          <TextField
            label="Template Name"
            value={templateName}
            onChange={(e) => setTemplateName(e.target.value)}
            fullWidth
            margin="normal"
          />
       <Autocomplete
  options={vendors}
  getOptionLabel={(option) => `${option.vendor_code} - ${option.vendor_name}`} // Display code and name
  onChange={(event, newValue) => setVendor(newValue ? newValue : null)} // Set to null if cleared
  renderInput={(params) => <TextField {...params} label="Vendor" margin="normal" fullWidth />}
  value={vendor} // Controlled value
/>


      

<FormControlLabel
                control={
                    <Switch
                        checked={isDefault}
                        onChange={(e) => setIsDefault(e.target.checked)}
                        name="isDefault"
                    />
                }
                label="Set as Default"
                
            />

        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCreateDialog(false)}>Cancel</Button>
          <Button onClick={handleSaveNewTemplate}>Create</Button>
        </DialogActions>
      </Dialog>

      {/* Template Viewer */}
<Dialog
  open={openViewModal}
  onClose={() => setOpenViewModal(false)}
  maxWidth="lg"
  fullWidth
  PaperProps={{
    style: {
      backgroundColor: "white",
      borderRadius: "12px",
      padding: "16px",
    },
  }}
>
  <DialogTitle sx={{ fontFamily: '"SapFiori72", sans-serif' }}>
    {selectedTemplate ? selectedTemplate.template_name : "Template Details"}
    <IconButton
      aria-label="close"
      onClick={() => setOpenViewModal(false)}
      sx={{ position: "absolute", right: 8, top: 8 }}
    >
      <CloseIcon />
    </IconButton>
  </DialogTitle>
  <DialogContent>
    {selectedTemplate ? (
      <TemplateEditor templateId={selectedTemplate.id} />
    ) : (
      <p>No template selected</p>
    )}
  </DialogContent>
</Dialog>



<Dialog open={openDeleteDialog} onClose={handleDeleteCancel}>
  <DialogTitle>Confirm Deletion</DialogTitle>
  <DialogContent>
    <p>
      Are you sure you want to delete the template: {templateToDelete ? templateToDelete.template_name : "N/A"}?
    </p>
  </DialogContent>
  <DialogActions>
    <Button onClick={handleDeleteCancel} color="primary">
      Cancel
    </Button>
    <Button onClick={handleDeleteConfirm} color="secondary">
      Delete
    </Button>
  </DialogActions>
</Dialog>


    </>
  );
};

export default TemplatesCard;
